import type { EChartsOption } from 'echarts';

import { readChartTheme } from '@/app/reports/components/report-chart-colors';
import {
  formatChartAxisDate,
  formatChartAxisValue,
  formatReportValue,
  getSeriesTimeSpanMs,
  isTimeSeriesSeries,
  toChartData,
  type ReportSeriesPoint,
} from '@/app/reports/components/report-chart-utils';
import {
  collectCategories,
  groupByNamedSeries,
  hasNamedSeries,
  toSingleSeriesRows,
} from '@/app/reports/components/report-series-grouping';
import type { ReportChartKind } from '@/app/reports/components/ReportChart';

type BuildInput = {
  kind: ReportChartKind;
  series: ReportSeriesPoint[];
  currency?: string;
  isPercentage?: boolean;
  valueLabel: string;
};

type ChartTheme = ReturnType<typeof readChartTheme>;

const CARTESIAN_KINDS = new Set([
  'line', 'area', 'bar', 'horizontal_bar', 'stacked_bar', 'grouped_bar', 'scatter', 'funnel', 'waterfall', 'heatmap',
]);

const truncateLabel = (value: string, max = 28) =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

const axisStyle = (theme: ChartTheme) => ({
  axisLine: { lineStyle: { color: theme.border } },
  axisTick: { lineStyle: { color: theme.border } },
  splitLine: { lineStyle: { color: theme.border, type: 'dashed' as const } },
});

const buildGrid = (isTime: boolean, horizontal: boolean) => ({
  left: horizontal ? 12 : 48,
  right: 48,
  top: 56,
  bottom: isTime ? 72 : horizontal ? 28 : 40,
  containLabel: true,
});

const buildDataView = (theme: ChartTheme) => ({
  readOnly: true,
  title: 'Data',
  lang: ['Data view', 'Close', 'Refresh'],
  buttonColor: theme.palette[0],
  textColor: theme.fg,
  textareaColor: theme.card,
  textareaBorderColor: theme.border,
});

const buildToolbox = (kind: ReportChartKind, theme: ChartTheme): EChartsOption['toolbox'] => {
  const base = {
    show: true,
    right: 8,
    top: 0,
    itemSize: 16,
    itemGap: 10,
    iconStyle: { borderColor: theme.fg, borderWidth: 1.5 },
    emphasis: { iconStyle: { borderColor: theme.palette[0], borderWidth: 2 } },
    tooltip: { show: true, backgroundColor: theme.card, textStyle: { color: theme.fg, fontSize: 11 } },
  };
  const save = { title: 'Save', pixelRatio: 2, backgroundColor: theme.card };
  const restore = { title: 'Restore' };
  const zoom = { yAxisIndex: 'none' as const, title: { zoom: 'Zoom', back: 'Reset' } };

  if (kind === 'pie' || kind === 'donut') {
    return { ...base, feature: { restore, saveAsImage: save, dataView: buildDataView(theme) } };
  }
  if (kind === 'combo' || !CARTESIAN_KINDS.has(kind)) {
    return {
      ...base,
      feature: { dataZoom: zoom, restore, saveAsImage: save, dataView: buildDataView(theme) },
    };
  }
  return {
    ...base,
    feature: {
      dataZoom: zoom,
      restore,
      saveAsImage: save,
      magicType: { type: ['line', 'bar'], title: { line: 'Line', bar: 'Bar' } },
      dataView: buildDataView(theme),
    },
  };
};

const tooltipShell = (theme: ChartTheme) => ({
  confine: false,
  appendTo: typeof document !== 'undefined' ? document.body : undefined,
  enterable: true,
  hideDelay: 800,
  showDelay: 0,
  transitionDuration: 0,
  backgroundColor: theme.card,
  borderColor: theme.border,
  extraCssText: 'box-shadow:0 4px 16px rgba(0,0,0,.12);border-radius:8px;padding:8px 12px;pointer-events:auto;',
  textStyle: { color: theme.fg, fontSize: 12 },
});

const formatTooltipValue = (raw: unknown, currency?: string, isPercentage?: boolean) =>
  formatReportValue(Number(Array.isArray(raw) ? raw[1] : raw ?? 0), currency, isPercentage);

const buildItemTooltip = (theme: ChartTheme, currency?: string, isPercentage?: boolean) => ({
  ...tooltipShell(theme),
  trigger: 'item' as const,
  triggerOn: 'mousemove|click|mousewheel' as const,
  formatter: (params: unknown) => {
    const item = (Array.isArray(params) ? params[0] : params) as {
      name?: string;
      seriesName?: string;
      value?: unknown;
      percent?: number;
    };
    const label = item.name ?? item.seriesName ?? '';
    const formatted = formatTooltipValue(item.value, currency, isPercentage);
    const pct = item.percent != null ? ` (${Math.round(item.percent)}%)` : '';
    return `<strong>${label}</strong><br/>${formatted}${pct}`;
  },
});

const buildCartesianTooltip = (
  theme: ChartTheme,
  currency?: string,
  isPercentage?: boolean,
) => ({
  ...tooltipShell(theme),
  trigger: 'axis' as const,
  triggerOn: 'mousemove|click|mousewheel' as const,
  axisPointer: {
    type: 'shadow' as const,
    shadowStyle: { color: `${theme.palette[0]}28` },
    z: 0,
  },
  formatter: (params: unknown) => {
    const items = Array.isArray(params) ? params : [params];
    const header = (items[0] as { name?: string })?.name ?? '';
    const lines = items.map((entry) => {
      const row = entry as { seriesName?: string; value?: unknown; marker?: string };
      const formatted = formatTooltipValue(row.value, currency, isPercentage);
      return `${row.marker ?? ''} ${row.seriesName ?? ''}: <strong>${formatted}</strong>`;
    });
    return header ? `<strong>${header}</strong><br/>${lines.join('<br/>')}` : lines.join('<br/>');
  },
});

const buildCartesianOption = (
  input: BuildInput,
  horizontal: boolean,
  chartType: 'line' | 'bar' | 'area' | 'scatter',
  stacked = false,
): EChartsOption => {
  const theme = readChartTheme();
  const isTime = isTimeSeriesSeries(input.series);
  const categories = hasNamedSeries(input.series)
    ? collectCategories(input.series)
    : toChartData(input.series).map((row) => row.name);
  const grouped = hasNamedSeries(input.series)
    ? groupByNamedSeries(input.series, categories)
    : [{ name: input.valueLabel, values: toSingleSeriesRows(input.series).map((row) => row.value) }];
  const spanMs = getSeriesTimeSpanMs(toChartData(input.series));
  const labelFormatter = (value: string) => {
    const text = isTime ? formatChartAxisDate(value, spanMs) : value;
    return horizontal ? truncateLabel(text) : text;
  };
  const valueFormatter = (value: number) =>
    formatChartAxisValue(value, input.currency, input.isPercentage);

  const categoryAxis = {
    type: 'category' as const,
    data: categories,
    ...axisStyle(theme),
    axisLabel: { color: theme.muted, fontSize: 11, formatter: labelFormatter },
  };
  const valueAxis = {
    type: 'value' as const,
    ...axisStyle(theme),
    axisLabel: { color: theme.muted, fontSize: 11, formatter: valueFormatter },
  };

  return {
    color: theme.palette,
    backgroundColor: 'transparent',
    grid: buildGrid(isTime, horizontal),
    toolbox: buildToolbox(input.kind, theme),
    tooltip: buildCartesianTooltip(theme, input.currency, input.isPercentage),
    legend: grouped.length > 1 ? { top: 4, left: 8, textStyle: { color: theme.muted } } : undefined,
    dataZoom: isTime
      ? [
          { type: 'inside', start: 0, end: 100, zoomOnMouseWheel: false, moveOnMouseMove: false },
          { type: 'slider', height: 18, bottom: 6, borderColor: theme.border },
        ]
      : undefined,
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? categoryAxis : valueAxis,
    series: grouped.map((group, index) => ({
      name: group.name,
      type: chartType === 'area' ? 'line' : chartType,
      stack: stacked ? 'total' : undefined,
      areaStyle: chartType === 'area' ? { opacity: 0.2 } : undefined,
      smooth: chartType === 'line' || chartType === 'area',
      showSymbol: chartType !== 'bar',
      symbolSize: chartType === 'scatter' ? 8 : 6,
      triggerLineEvent: chartType === 'line' || chartType === 'area',
      barMaxWidth: chartType === 'bar' ? 48 : undefined,
      itemStyle: { color: theme.palette[index % theme.palette.length] },
      emphasis: { focus: 'none' as const },
      data: group.values,
    })),
  };
};

const buildPieOption = (input: BuildInput, donut: boolean): EChartsOption => {
  const theme = readChartTheme();
  const rows = toSingleSeriesRows(input.series);
  return {
    color: theme.palette,
    backgroundColor: 'transparent',
    toolbox: buildToolbox(input.kind, theme),
    tooltip: buildItemTooltip(theme, input.currency, input.isPercentage),
    legend: { type: 'scroll', bottom: 0, textStyle: { color: theme.muted } },
    series: [{
      type: 'pie',
      radius: donut ? ['42%', '68%'] : '68%',
      center: ['50%', '46%'],
      itemStyle: { borderRadius: 4, borderColor: theme.card, borderWidth: 2 },
      label: { color: theme.muted, fontSize: 11 },
      data: rows.map((row) => ({ name: row.name, value: row.value })),
    }],
  };
};

export const buildReportEChartOption = (input: BuildInput): EChartsOption => {
  if (input.kind === 'pie' || input.kind === 'donut') return buildPieOption(input, input.kind === 'donut');
  if (input.kind === 'combo') return buildCartesianOption(input, false, 'bar');
  if (input.kind === 'horizontal_bar' || input.kind === 'funnel') return buildCartesianOption(input, true, 'bar');
  if (input.kind === 'stacked_bar') return buildCartesianOption(input, false, 'bar', true);
  if (input.kind === 'area' || input.kind === 'sparkline') return buildCartesianOption(input, false, 'area');
  if (input.kind === 'scatter') return buildCartesianOption(input, false, 'scatter');
  if (input.kind === 'line') return buildCartesianOption(input, false, 'line');
  return buildCartesianOption(input, false, 'bar');
};
